import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@material-ui/core";
import StoreOutlinedIcon from "@material-ui/icons/StoreOutlined";
import Button from "../Button/Button";
import TextInput from "../TextInput/TextInput";
import User from "../../../../passapp-sdk/user";
import { useStore } from "../../../../hooks/useStore";
import { formatGuardiaFecha } from "../Table/utils/formatDate";
import "./EditAssignedDockDialog.css";

/** Datos mínimos para editar un dock, disponibles tanto en la vista por persona como por vehículo. */
export type EditDockTarget = {
  eventId?: string;
  nombre: string;
  transportCompanyName?: string | null;
  patente?: string;
  fecha: string;
  dock?: string | number | null;
};

type EditAssignedDockDialogProps = {
  target: EditDockTarget | null;
  onClose: () => void;
  onUpdated?: () => void;
};

/** Edición del dock de un transporte que ya registró su ingreso. */
const EditAssignedDockDialog: React.FC<EditAssignedDockDialogProps> = ({
  target,
  onClose,
  onUpdated,
}) => {
  const { authStore } = useStore();
  const userSdk = useMemo(() => new User(), []);
  const [dock, setDock] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setDock(target ? String(target.dock ?? "") : "");
    setError("");
  }, [target]);

  if (!target) return null;

  const { day, time } = formatGuardiaFecha(target.fecha);
  const registeredAt = [day, time].filter(Boolean).join(" a las ");
  const vehicleDescription = [target.nombre, target.transportCompanyName].filter(Boolean).join(" · ");

  const handleSave = async () => {
    const nextDock = dock.trim();
    if (!nextDock) {
      setError("Ingresá un número de dock.");
      return;
    }
    if (!authStore.token || !target.eventId) {
      setError("No se pudo identificar el evento de ingreso.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const response = await userSdk.updateEventDock(authStore.token, target.eventId, nextDock);
      if (response?.data !== true) {
        throw new Error(response?.msg || "No se pudo actualizar el dock.");
      }
      onUpdated?.();
      onClose();
    } catch (requestError) {
      setError(
        typeof requestError === "string"
          ? requestError
          : requestError instanceof Error
          ? requestError.message
          : "No se pudo actualizar el dock. Intentá nuevamente."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: "edit-dock-dialog" }}
    >
      <DialogTitle disableTypography className="edit-dock-dialog-title">
        <h2>Editar dock asignado</h2>
        {vehicleDescription && <p>{vehicleDescription}</p>}
      </DialogTitle>
      <DialogContent className="edit-dock-dialog-content">
        <div className="edit-dock-dialog-entry">
          {target.patente && <span className="edit-dock-dialog-plate">{target.patente}</span>}
          {registeredAt && <Typography>Registrado el {registeredAt}</Typography>}
        </div>

        <TextInput
          label="Dock *"
          value={dock}
          onChange={setDock}
          placeholder="Ingresá el número de dock"
          leftIcon={<StoreOutlinedIcon />}
          error={error}
          disabled={saving}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleSave();
          }}
        />

        <div className="edit-dock-dialog-note">
          El vehículo ya está dentro. El cambio de dock queda registrado en el evento con tu usuario
          y la hora.
        </div>
      </DialogContent>
      <DialogActions className="edit-dock-dialog-actions">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="neutral" onClick={handleSave} disabled={saving} loading={saving}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAssignedDockDialog;
